import {
  EquipmentSlot,
  InventoryItem,
  QuickSlot,
} from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { DragOverEvent, useDndMonitor, useDroppable } from "@dnd-kit/core";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import {
  AccessoriesIcon,
  Bag2Icon,
  BodyArmorIcon,
  BraceletsIcon,
  EarsIcon,
  GlassesIcon,
  GlovesIcon,
  Hands2Icon,
  HatIcon,
  LockIcon,
  MaskIcon,
  Shoes2Icon,
  TopsIcon,
  WatchersIcon,
} from "@/shared/icons";
import { BaseIconProps } from "@/shared/icons/BaseIcon";
import { Legs2Icon } from "@/shared/icons/Legs2Icon";
import { EquipSlot } from "@/domains/inventory/constants/equipSlot";
import { ItemType } from "@/domains/inventory/constants/itemType";
import { determineEquipmentSlotType } from "@/domains/inventory/helpers/determineEquipmentSlotType";

type Props = {
  equipmentId?: number;
  label?: string;
  slot?: EquipSlot;
  quickSlot?: number;
  isLocked?: boolean;
  children?: ReactNode;
  onEquip?: (equipmentId: number, slot: EquipmentSlot) => Promise<void>;
  onAssignQuickSlot?: (
    item: QuickSlot,
    from?: {
      slot?: EquipSlot;
      quickSlot?: number;
    },
  ) => Promise<void>;
  onRefresh?: () => Promise<void> | void;
  className?: string;
  classNameIcon?: string;
  classNameLabel?: string;
  disabled?: boolean;
};

const iconSlot: Record<EquipSlot, FC<BaseIconProps>> = {
  [EquipSlot.Ears]: EarsIcon,
  [EquipSlot.Watches]: WatchersIcon,
  [EquipSlot.Mask]: MaskIcon,
  [EquipSlot.Shoes]: Shoes2Icon,
  [EquipSlot.Accessories]: AccessoriesIcon,
  [EquipSlot.Hands]: Hands2Icon,
  [EquipSlot.Bracelets]: BraceletsIcon,
  [EquipSlot.Legs]: Legs2Icon,
  [EquipSlot.Bags]: Bag2Icon,
  [EquipSlot.BodyArmors]: BodyArmorIcon,
  [EquipSlot.Tops]: TopsIcon,
  [EquipSlot.Hat]: HatIcon,
  [EquipSlot.Glasses]: GlassesIcon,
  [EquipSlot.Gloves]: GlovesIcon,
};

export default function InventorySlotCell({
  equipmentId,
  slot,
  label,
  quickSlot,
  isLocked,
  children,
  onEquip,
  onAssignQuickSlot,
  onRefresh,
  className,
  classNameIcon,
  classNameLabel,
  disabled,
}: Props) {
  const id = useMemo(() => {
    if (slot) return `equip-${slot}-${equipmentId}`;
    if (quickSlot) return `quick-${quickSlot}-${equipmentId}`;
    return `drop-zone-${equipmentId}`;
  }, [slot, quickSlot]);

  const { isOver, setNodeRef: dropRef } = useDroppable({
    id,
    data: { type: "inventory-slot", slot, quickSlot },
    disabled,
  });

  const [canDrop, setCanDrop] = useState(false);

  const canDropCheck = useCallback(
    (event: DragOverEvent): boolean => {
      const { active } = event;
      if (!active?.data?.current) {
        return false;
      }
      const item = active.data.current as InventoryItem;

      if (slot) {
        const determinedEquipmentSlotType = determineEquipmentSlotType(
          item.itemTemplate.type as ItemType,
        );
        return determinedEquipmentSlotType === slot;
      }

      if (quickSlot) {
        return item.itemTemplate.isQuick;
      }

      return true;
    },
    [slot, quickSlot],
  );

  useDndMonitor({
    onDragEnd: async (event) => {
      const { active, over } = event;
      const activeItem = active?.data?.current as InventoryItem;
      const droppable = canDropCheck(event);

      if (!activeItem || over?.id !== id || isLocked || !droppable) return;

      const payload = activeItem?.payload;

      if (slot && equipmentId) {
        if (payload && payload?.slot && payload?.slot === slot) return;
        await onEquip?.(equipmentId, {
          slot,
          item: activeItem,
        });
      }

      if (quickSlot) {
        if (payload && payload?.quick && payload?.quickSlot === String(quickSlot)) return;
        await onAssignQuickSlot?.(
          {
            id: quickSlot,
            slotNumber: quickSlot,
            inventoryItem: activeItem,
          },
          {
            quickSlot: payload?.quickSlot,
          },
        );
      }

      await onRefresh?.();
    },
    onDragOver: (event) => {
      setCanDrop(canDropCheck(event));
    },
  });

  const iconComponents = useMemo(() => {
    if (isLocked) {
      return <LockIcon className={clsx("size-1/2", classNameIcon)} />;
    }
    if (slot) {
      return iconSlot[slot]({ className: clsx("size-1/2", classNameIcon) });
    }
    return null;
  }, []);

  return (
    <div
      ref={dropRef}
      data-mz-accent={canDrop ? "success" : "accent"}
      className={cn(
        "relative w-[4.375rem] h-[4.375rem]",
        "rounded-[0.625rem] border-[2px] bg-clip-content box-border",
        "bg-dark-800/20 border-[#1A1A1A] text-[#434242]",
        {
          "bg-gradient-to-t from-accent-solid/10 to-dark-800/20 text-accent-solid/50 border-accent-solid/10":
            isOver && !isLocked,
          "opacity-50": isLocked,
        },
        className,
      )}
    >
      <div className="absolute -inset-0.5 rounded-[inherit] flex flex-col gap-1.5 items-center justify-center">
        {children || iconComponents}
      </div>
      {!children && label && (
        <div
          className={cn(
            "absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-11 font-medium",
            children ? "text-white" : "text-white/50",
            classNameLabel,
          )}
        >
          {label}
        </div>
      )}
      {!!quickSlot && (
        <div className="absolute text-11 font-bold bottom-1.5 left-2">{quickSlot}</div>
      )}
    </div>
  );
}
